import { useEffect, useMemo, useState } from 'react';
import Star from "../../assets/icons-redesign/ai-star.svg?react";
import AiSampleSize from './ai-sample-size';
import { Dialog } from 'primereact/dialog';
import { isQA } from 'src/helpers/env.helpers';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { ChevronDown } from 'lucide-react';
import {
  SurveyAudiencePanelField,
  SurveyAudienceWithPanelFields,
  useGetSurveyAudiencesWithPanelFieldsQuery,
  usePreviewSurveyAudienceMutation,
} from 'src/redux/survey/survey.endpoints';

interface CriteriaCardProps {
  extSurveyId: string;
}

const SurveyResponderTrigger = ({ extSurveyId }: CriteriaCardProps) => {
  const { data: audiences } = useGetSurveyAudiencesWithPanelFieldsQuery(null);
  const [selectedAudience, setSelectedAudience] = useState<SurveyAudienceWithPanelFields | null>(null);
  const [fieldSelections, setFieldSelections] = useState<Record<string, string[]>>({});
  const [fieldSearchTerms, setFieldSearchTerms] = useState<Record<string, string>>({});

  let ai: number[] = [];
  if (isQA()) {
    ai = [0, 10, 50, 100, 200];
  } else {
    ai = [0, 100, 200, 500, 750, 1000];
  }

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const iconStroke = 'white';

  const aiAudiences =
    audiences?.filter(
      (a) => a.panel_type === 'ai' && a.status === 'active' && a.locked === true
    ) ?? [];

  const getSelectedPanelId = (audience: SurveyAudienceWithPanelFields | null) => {
    if (!audience) return '';
    return audience.id ?? audience.panel_fields?.[0]?.panel ?? '';
  };

  const getFieldKey = (field: SurveyAudiencePanelField) => field.name || field.id;

  const toggleMultiValue = (field: SurveyAudiencePanelField, value: string) => {
    const key = getFieldKey(field);
    setFieldSelections((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const setSingleValue = (field: SurveyAudiencePanelField, value: string) => {
    const key = getFieldKey(field);
    setFieldSelections((prev) => ({ ...prev, [key]: [value] }));
  };

  const handleFieldSearchChange = (fieldKey: string, value: string) => {
    setFieldSearchTerms((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const audienceCriteria = useMemo(() => {
    if (!selectedAudience) return {};

    const allowedFieldKeys = new Set(
      selectedAudience.panel_fields.map((field) => getFieldKey(field))
    );

    return Object.fromEntries(
      Object.entries(fieldSelections).filter(
        ([key, values]) => allowedFieldKeys.has(key) && values.length > 0
      )
    );
  }, [selectedAudience, fieldSelections]);

  const selectedPanelId = useMemo(() => {
    if (!selectedAudience) return '';
    return getSelectedPanelId(selectedAudience);
  }, [selectedAudience]);

  const fieldDisplayNameByKey = useMemo(() => {
    if (!selectedAudience) return {};

    return Object.fromEntries(
      selectedAudience.panel_fields.map((field) => [
        getFieldKey(field),
        field.display_name || field.name,
      ])
    );
  }, [selectedAudience]);

  const [
    previewAudience,
    {
      data: audiencePreview,
      isLoading: isPreviewLoading,
    },
  ] = usePreviewSurveyAudienceMutation();

  console.log(audiencePreview);
  useEffect(() => {
    if (!selectedAudience) return;
    if (!selectedPanelId) return;

    const timeout = setTimeout(() => {
      previewAudience({
        panel: selectedPanelId,
        criteria: audienceCriteria as Record<string, string[]>,
      });
    }, 400);

    return () => clearTimeout(timeout);
  }, [selectedAudience, selectedPanelId, audienceCriteria, previewAudience]);

  const selectedCriteriaSummary = useMemo(() => {
    const entries = Object.entries(audienceCriteria);
    if (!entries.length) return '';

    const formatValues = (values: string[]) => {
      const first = values.slice(0, 3);
      const remaining = values.length - first.length;
      if (remaining <= 0) return first.join(', ');
      return `${first.join(', ')} +${remaining} more`;
    };

    return entries
      .map(([key, values]) => {
        const label = fieldDisplayNameByKey[key] ?? key;
        return `${label}: ${formatValues(values)}`;
      })
      .join(' · ');
  }, [audienceCriteria, fieldDisplayNameByKey]);

  const estimatedPanelistPercent = useMemo(() => {
    if (!audiencePreview) return null;
    const previewCount = Number(audiencePreview.preview_count ?? 0);
    const totalPanelists = Number(audiencePreview.total_panelists ?? 0);
    if (!totalPanelists) return null;
    return Math.round((previewCount / totalPanelists) * 100);
  }, [audiencePreview]);

  return (
    <div>
      <button
        className="bg-purple-500 text-white px-3 py-2 h-[40px] rounded-full relative z-50"
        onClick={() => setIsOpen(true)}
      >
        <div className='cursor-pointer flex items-center gap-1.5 text-sm md:text-base font-medium'>
          <Star stroke={iconStroke} />
          {/* AI Panelists */}
        </div>
      </button>

      <Dialog
        visible={isOpen}
        className='w-1/2 max-w-3/4'
        onHide={() => setIsOpen(false)}
      >
        <div className="p-0">

          <div className="px-4 pb-4">
            <h2 className="text-lg font-medium">
              AI Panelists
            </h2>
            <div className="mt-2">
              <DropdownMenu
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
                modal={false}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between p-4 rounded-lg m-0"
                    size="med"
                  >
                    {selectedAudience ? selectedAudience.name : 'Select Audience'}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="z-[9999]">
                  {aiAudiences.map((audience) => (
                    <DropdownMenuItem
                      key={audience.id}
                      onClick={() => {
                        setSelectedAudience(audience);
                        setFieldSelections({});
                        setFieldSearchTerms({});
                      }}
                    >
                      <div className="flex flex-col gap-2 border-b border-gray-200 pb-2">
                        <span className="font-medium ">
                          {audience.name}
                        </span>
                        <div className="flex justify-between gap-2">
                          <span className="text-xs text-gray-500 mt-0">
                            {audience.panel_fields?.length ?? 0}
                            {' '}
                            fields
                          </span>
                          <span className="text-xs text-gray-500 mt-0">
                            {audience.panel_fields.filter((field) => field.status === 'active').length}
                            {' '}
                            active
                          </span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="px-4 pb-4">
            {selectedAudience && selectedAudience.panel_fields.length > 0 && (
              <div className="my-3 border border-gray-200 rounded-md p-3">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Panel Filters
                </div>
                <div className="max-h-[230px] overflow-y-auto pr-2">
                  {selectedAudience.panel_fields
                    .filter((field) => field.status === 'active' || field.status === undefined)
                    .map((field) => {
                      const control = field.selector_configuration?.control;
                      const options = field.selector_configuration?.options ?? [];
                      const selectionMode = control?.selection ?? 'single';
                      const isMulti = selectionMode === 'multi';
                      const fieldKey = getFieldKey(field);
                      const selectedValues = fieldSelections[fieldKey] ?? [];
                      const searchValue = fieldSearchTerms[fieldKey] ?? '';
                      const normalizedQuery = searchValue.trim().toLowerCase();
                      const shouldShowSearch = options.length > 10;

                      const filteredOptions = !shouldShowSearch || !normalizedQuery
                        ? options
                        : options.filter((opt) => {
                          const label = opt.label ?? '';
                          const value = opt.value ?? '';
                          return (
                            label.toLowerCase().includes(normalizedQuery)
                            || value.toLowerCase().includes(normalizedQuery)
                          );
                        });

                      if (!options.length) return null;

                      return (
                        <details
                          key={field.id}
                          className="group mb-2 border border-gray-200 rounded-md p-2 bg-white"
                        >
                          <summary className="cursor-pointer select-none flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-800">
                              {field.display_name || field.name}
                            </span>
                            <span className="text-xs text-gray-500 ml-3">
                              {selectedValues.length ? selectedValues.length : 'None'}
                            </span>
                          </summary>

                          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                            {shouldShowSearch && (
                              <div className="w-full mb-2">
                                <input
                                  type="text"
                                  value={searchValue}
                                  onChange={(e) => handleFieldSearchChange(fieldKey, e.target.value)}
                                  placeholder="Search options..."
                                  className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm outline-none focus:border-blue-400"
                                />
                              </div>
                            )}

                            {filteredOptions.length === 0 ? (
                              <div className="text-xs text-gray-500 pl-1 py-1">
                                No matching options
                              </div>
                            ) : (
                              filteredOptions.map((opt) => {
                                const checked = selectedValues.includes(opt.value);

                                if (!isMulti) {
                                  return (
                                    <label
                                      key={opt.value}
                                      className="flex items-center gap-2 text-sm text-gray-700"
                                    >
                                      <input
                                        type="radio"
                                        name={`radio_${field.id}`}
                                        checked={checked}
                                        onChange={() => setSingleValue(field, opt.value)}
                                      />
                                      <span>
                                        {opt.label}
                                      </span>
                                    </label>
                                  );
                                }

                                return (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-2 text-sm text-gray-700"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleMultiValue(field, opt.value)}
                                    />
                                    <span>
                                      {opt.label}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </details>
                      );
                    })}
                </div>
              </div>
            )}

            {selectedAudience && selectedPanelId && (
              <div className="my-3 border border-gray-200 rounded-md p-3 bg-white">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Estimated panelists
                </div>
                <div className="text-xs text-gray-600 mb-2 break-words">
                  {selectedCriteriaSummary
                    ? `Selected: ${selectedCriteriaSummary}`
                    : 'No filters selected'}
                </div>
                <div className="text-sm text-gray-800">
                  {isPreviewLoading ? (
                    'Estimating...'
                  ) : audiencePreview ? (
                    <>
                      {Number(audiencePreview.preview_count ?? 0).toLocaleString()}
                      {' / '}
                      {Number(audiencePreview.total_panelists ?? 0).toLocaleString()}
                      {estimatedPanelistPercent != null
                        ? ` (~${estimatedPanelistPercent}%)`
                        : ''}
                    </>
                  ) : (
                    '--'
                  )}
                </div>
              </div>
            )}

            {selectedAudience && (
              <AiSampleSize
                extSurveyId={extSurveyId}
                sampleSizes={ai}
                panelId={getSelectedPanelId(selectedAudience)}
                audienceCriteria={audienceCriteria}
                zeroDisabled={false}
                onSubmit={() => setIsOpen(false)}
              />
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default SurveyResponderTrigger;
